import "./price-range-filter";

const SIZE_PARAM = "filter.v.option.size";
const AVAILABILITY_PARAM = "filter.v.availability";

const debounce = (fn, wait) => {
  let t;
  const debounced = (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => clearTimeout(t);
  return debounced;
};

/**
 * Filter & Sort form. Changes apply live, except while the filter drawer is
 * open as a modal (mobile): there they are staged, and committed in one render
 * when the drawer closes — by Apply, the X, Escape, or anything else.
 */
class FacetFiltersForm extends HTMLElement {
  debouncedOnSubmit = debounce(() => this.onSubmitHandler(), 500);
  debouncedPrefetch = debounce(() => this.prefetchStaged(), 300);

  // Params when the modal drawer opened; null while changes apply live.
  stagedFrom = null;

  handleFormInput = (e) => {
    this.lastInputEvent = e;
    this.syncAvailabilityLock();
    this.syncActiveStates();
    this.renderDrawerActiveFilters();

    if (this.stagedFrom !== null) {
      this.updateSecondaryAction();
      this.debouncedPrefetch();
      return;
    }

    this.debouncedOnSubmit();
  };

  handleDrawerOpening = (e) => {
    this.stagedFrom = e.detail.modal ? this.getSearchParams() : null;
    if (e.detail.modal) this.syncUnderHeader();
  };

  handleDrawerClosing = () => {
    if (this.stagedFrom === null) return;

    const stagedFrom = this.stagedFrom;
    this.stagedFrom = null;
    this.debouncedPrefetch.cancel();

    const searchParams = this.getSearchParams();
    if (searchParams !== stagedFrom) FacetFiltersForm.renderPage(searchParams);
  };

  handleSecondaryAction = (e) => {
    if (!e.target.closest("[data-secondary-action]")) return;

    if (this.hasActiveFilters()) {
      this.clearFilters();
    } else {
      this.querySelector("filter-drawer")?.close();
    }
  };

  connectedCallback() {
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.facetForm = this.querySelector("form");
    if (!this.facetForm) return;

    this.facetForm.addEventListener("input", this.handleFormInput);
    this.addEventListener("filter-drawer:opening", this.handleDrawerOpening);
    this.addEventListener("filter-drawer:closing", this.handleDrawerClosing);
    this.addEventListener("click", this.handleSecondaryAction);
    this.renderDrawerActiveFilters();
    this.observeStickyOffset();
  }

  disconnectedCallback() {
    this.facetForm?.removeEventListener("input", this.handleFormInput);
    this.removeEventListener("filter-drawer:opening", this.handleDrawerOpening);
    this.removeEventListener("filter-drawer:closing", this.handleDrawerClosing);
    this.removeEventListener("click", this.handleSecondaryAction);
    this.stickyOffsetObserver?.disconnect();
  }

  /**
   * Opening the drawer lifts the section above the header, which would reveal
   * the chips and active filters scrolled up under it. Flags that case so CSS
   * can hide them while the drawer is open.
   */
  syncUnderHeader() {
    const headerHeight =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-height",
        ),
      ) || 0;
    this.classList.toggle(
      "facets--under-header",
      this.getBoundingClientRect().top < headerHeight,
    );
  }

  /**
   * Publishes the toolbar's distance from the top of the sticky section as
   * `--facets-sticky-offset`, so mobile can stick the section with everything
   * above the toolbar (chips, active filters) scrolled up under the header.
   */
  observeStickyOffset() {
    const sticky = this.closest(
      ".collection-filtering, .main-collection__facets",
    );
    const toolbar = this.querySelector(".facets__toolbar");
    if (!sticky || !toolbar) return;

    const update = () => {
      const offset =
        toolbar.getBoundingClientRect().top -
        sticky.getBoundingClientRect().top;
      sticky.style.setProperty("--facets-sticky-offset", `${offset}px`);
    };

    this.stickyOffsetObserver = new ResizeObserver(update);
    this.stickyOffsetObserver.observe(this.facetForm);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  /**
   * Adds In Stock Online whenever a size is selected, so the size filter
   * returns products whose variant in that size is in stock. The Liquid
   * redirect in main-collection covers URLs that arrive without it.
   */
  static normalizeSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams);
    const form = document.getElementById("FacetFiltersForm");
    const requiresAvailability = form?.hasAttribute(
      "data-size-requires-availability",
    );

    if (
      requiresAvailability &&
      params.has(SIZE_PARAM) &&
      !params.has(AVAILABILITY_PARAM)
    ) {
      params.append(AVAILABILITY_PARAM, "1");
    }

    // One canonical string per filter state, so the cache hits however the
    // params were produced — form order, a remove link, or the page URL.
    if (params.get("sort_by") === form?.dataset.defaultSort) {
      params.delete("sort_by");
    }
    params.sort();

    return params.toString();
  }

  /**
   * The facets and the product grid can live in different sections (Collection
   * Filtering + Main Collection) or the same one (search), so both are
   * requested in one Section Rendering API call.
   */
  static sectionUrl(searchParams) {
    const sectionIds = new Set(
      [
        document.getElementById("product-grid-section")?.dataset.id,
        document.querySelector("facet-filters-form")?.dataset.sectionId,
      ].filter(Boolean),
    );
    return `${window.location.pathname}?sections=${[...sectionIds].join(",")}&${searchParams}`;
  }

  /**
   * Resolves to every requested section's markup joined into one string, so it
   * parses as a single document. Cached by URL; in-flight requests are shared,
   * so a prefetch is never repeated.
   */
  static fetchSection(url) {
    if (!FacetFiltersForm.filterData.has(url)) {
      FacetFiltersForm.filterData.set(
        url,
        fetch(url)
          .then((response) => response.json())
          .then((sections) => Object.values(sections).join(""))
          .catch((error) => {
            FacetFiltersForm.filterData.delete(url);
            throw error;
          }),
      );
    }

    return FacetFiltersForm.filterData.get(url);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.normalizeSearchParams(searchParams);

    // The state being left may never have been fetched (the server rendered the
    // first page), so cache it now and going back to it is instant.
    FacetFiltersForm.fetchSection(
      FacetFiltersForm.sectionUrl(FacetFiltersForm.searchParamsPrev),
    ).catch(() => {});

    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.setLoading();

    FacetFiltersForm.fetchSection(FacetFiltersForm.sectionUrl(searchParams))
      .then((html) => {
        // A newer render started while this one was in flight.
        if (FacetFiltersForm.searchParamsPrev !== searchParams) return;

        const parsedHTML = new DOMParser().parseFromString(html, "text/html");
        FacetFiltersForm.renderFilters(parsedHTML, event);
        FacetFiltersForm.renderProductGridContainer(parsedHTML);
      })
      .catch(() => {
        window.location.assign(`${window.location.pathname}?${searchParams}`);
      })
      .finally(() => {
        if (FacetFiltersForm.searchParamsPrev === searchParams) {
          FacetFiltersForm.setDoneLoading();
        }
      });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderProductGridContainer(parsedHTML) {
    const target = document.getElementById("ProductGridContainer");
    const source = parsedHTML.getElementById("ProductGridContainer");
    if (target && source) target.innerHTML = source.innerHTML;
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter",
    );
    const jsFilter = event?.target?.closest?.(".js-filter");
    const matchesIndex = (element) =>
      jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element),
    );

    facetsToRender.forEach((element) => {
      const target = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`,
      );
      if (!target) return;

      // Keep open mobile accordions open across the re-render.
      const details = element.querySelector(".facets__group-details");
      const targetDetails = target.querySelector(".facets__group-details");
      if (details && targetDetails?.open) {
        details.setAttribute("open", "");
        details.classList.add("open");
      }

      target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.renderClearAll(parsedHTML);

    const form = document.querySelector("facet-filters-form");
    form?.syncActiveStates();
    form?.renderDrawerActiveFilters();
    form?.updateSecondaryAction();
  }

  static renderAdditionalElements(parsedHTML) {
    parsedHTML.querySelectorAll("[data-render]").forEach((el) => {
      const target = document.querySelector(
        `[data-render="${el.dataset.render}"]`,
      );
      if (!target) return;

      target.innerHTML = el.innerHTML;
    });
  }

  /** Syncs every Clear All on the page, including those outside this section. */
  static renderClearAll(parsedHTML) {
    const source = parsedHTML.querySelector("[data-facets-clear-all]");
    if (!source) return;

    const href = source.querySelector("a")?.getAttribute("href");

    document.querySelectorAll("[data-facets-clear-all]").forEach((el) => {
      el.hidden = source.hidden;
      if (href) el.querySelector("a")?.setAttribute("href", href);
    });
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  static setLoading() {
    document.querySelector("[data-product-grid]")?.classList.add("loading");
  }

  static setDoneLoading() {
    document.querySelector("[data-product-grid]")?.classList.remove("loading");
  }

  getSearchParams() {
    return FacetFiltersForm.normalizeSearchParams(
      new URLSearchParams(new FormData(this.facetForm)).toString(),
    );
  }

  hasActiveFilters() {
    return [...new FormData(this.facetForm).keys()].some((key) =>
      key.startsWith("filter."),
    );
  }

  /**
   * Mirrors the Liquid lock: a selected size forces In Stock Online on. Once
   * no size is selected the toggle unlocks and keeps its checked state.
   */
  syncAvailabilityLock() {
    const toggle = this.querySelector("[data-availability-toggle]");
    if (!toggle) return;

    const sizeSelected = !!this.facetForm.querySelector(
      `input[name="${SIZE_PARAM}"]:checked`,
    );

    if (sizeSelected) toggle.checked = true;
    toggle.disabled = sizeSelected;
  }

  /**
   * Mirrors the Liquid `is-active` classes onto the current, possibly staged,
   * selection — options, groups (with their count), category chips and the
   * Filter & Sort button. Sort always has a value, so it never marks a group.
   */
  syncActiveStates() {
    const activeInput = '.facets__option-input:checked:not([name="sort_by"])';

    this.querySelectorAll(".facets__option").forEach((option) => {
      const input = option.querySelector(".facets__option-input");
      option.classList.toggle("is-active", !!input?.checked);
    });

    this.querySelectorAll(".facets__group").forEach((group) => {
      group.classList.toggle("is-active", !!group.querySelector(activeInput));
    });

    this.querySelector(".facets__category-chips")?.classList.toggle(
      "is-active",
      !!this.querySelector(`.facets__category-chips ${activeInput}`),
    );

    this.querySelector(".facets__filter-button")?.classList.toggle(
      "is-active",
      !!this.querySelector(`filter-drawer ${activeInput}`),
    );
  }

  /**
   * Removal chips at the top of the mobile drawer, one per checked input, so
   * they include staged choices. Removing one unchecks its input, which is
   * then staged or applied like any other change.
   */
  renderDrawerActiveFilters() {
    const container = this.querySelector("[data-drawer-active-filters]");
    const template = this.querySelector(
      "template[data-drawer-active-filter-template]",
    );
    if (!container || !template) return;

    // A size-locked availability toggle can't be removed on its own.
    const inputs = [
      ...this.querySelectorAll(
        'filter-drawer .facets__option-input:checked:not([name="sort_by"]):not(:disabled)',
      ),
    ];
    const focusedIndex = [...container.children].indexOf(
      document.activeElement,
    );

    const chips = inputs.map((input) => {
      const chip = template.content.firstElementChild.cloneNode(true);
      const option = input.closest(".facets__option");
      const swatch = option?.querySelector(".facets__swatch");
      if (swatch) chip.prepend(swatch.cloneNode(true));
      chip.querySelector("[data-label]").textContent =
        option?.querySelector(".facets__option-label")?.textContent.trim() ??
        "";
      chip.addEventListener("click", () => {
        input.checked = false;
        input.dispatchEvent(new Event("input", { bubbles: true }));
      });
      return chip;
    });

    container.replaceChildren(...chips);
    container.hidden = chips.length === 0;

    // Removing a chip rebuilds the list; keep focus in it for keyboard users.
    if (focusedIndex !== -1 && chips.length) {
      chips[Math.min(focusedIndex, chips.length - 1)].focus();
    }
  }

  /** The drawer's secondary button reads "Clear All" while any filter is selected, "Close" otherwise. */
  updateSecondaryAction() {
    const button = this.querySelector("[data-secondary-action]");
    if (!button) return;

    const hasFilters = this.hasActiveFilters();
    button.toggleAttribute("data-has-filters", hasFilters);
    button.textContent = hasFilters
      ? button.dataset.labelClear
      : button.dataset.labelClose;
  }

  /** Clears every filter but keeps the sort. Staged like any other change while the drawer is a modal. */
  clearFilters() {
    this.facetForm
      .querySelectorAll('input[type="checkbox"]:checked')
      .forEach((input) => {
        input.checked = false;
      });
    this.querySelectorAll("price-range-filter").forEach((el) => el.sync());

    this.handleFormInput();
  }

  /** Caches the staged results ahead of the commit, so closing the drawer renders them instantly. */
  prefetchStaged() {
    FacetFiltersForm.fetchSection(
      FacetFiltersForm.sectionUrl(this.getSearchParams()),
    ).catch(() => {});
  }

  onSubmitHandler() {
    FacetFiltersForm.renderPage(this.getSearchParams(), this.lastInputEvent);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const href = event.currentTarget.href;
    const searchParams = href.includes("?")
      ? href.slice(href.indexOf("?") + 1)
      : "";
    FacetFiltersForm.renderPage(searchParams);
  }
}

FacetFiltersForm.filterData = new Map();
FacetFiltersForm.searchParamsInitial = FacetFiltersForm.normalizeSearchParams(
  window.location.search.slice(1),
);
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
if (!customElements.get("facet-filters-form")) {
  customElements.define("facet-filters-form", FacetFiltersForm);
  FacetFiltersForm.setListeners();
}

class FacetRemove extends HTMLElement {
  handleClick = (e) => this.closeFilter(e);

  handleKeyup = (e) => {
    if (e.code.toUpperCase() !== "SPACE") return;

    e.preventDefault();
    this.closeFilter(e);
  };

  connectedCallback() {
    this.facetLink = this.querySelector("a");
    if (!this.facetLink) return;

    this.facetLink.setAttribute("role", "button");
    this.facetLink.addEventListener("click", this.handleClick);
    this.facetLink.addEventListener("keyup", this.handleKeyup);
  }

  disconnectedCallback() {
    this.facetLink?.removeEventListener("click", this.handleClick);
    this.facetLink?.removeEventListener("keyup", this.handleKeyup);
  }

  closeFilter(event) {
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");

    // No facets on this page (e.g. the section was removed): let the link navigate.
    if (!form) return;

    event.preventDefault();
    form.onActiveFilterClick(event);
  }
}

if (!customElements.get("facet-remove")) {
  customElements.define("facet-remove", FacetRemove);
}
